import React, { createContext, useContext, useState, useEffect } from 'react';

export const isBrowser = () => typeof window !== 'undefined';

// Firebase configuration
const firebaseConfig = {
  apiKey: process.env.FIREBASE_API_KEY,
  authDomain: process.env.FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.FIREBASE_DATABASE_URL,
  projectId: process.env.FIREBASE_PROJECT_ID
};

// Create Auth Context
const AuthContext = createContext({
  user: null,
  loading: true,
  isAuthenticated: false
});

// Initialize Firebase app and auth (lazy initialization to avoid SSR issues)
// These are only initialized in the browser, never during SSR
let app = null;
let auth = null;

const getAuthInstance = () => {
  // Double-check browser environment
  if (typeof window === 'undefined') {
    return null;
  }
  
  try {
    if (!auth) {
      // Lazy import Firebase to avoid SSR issues
      // Using dynamic import with try-catch to handle any import errors
      const firebaseApp = require('firebase/app');
      const firebaseAuth = require('firebase/auth');
      
      if (!app) {
        app = firebaseApp.initializeApp(firebaseConfig);
      }
      auth = firebaseAuth.getAuth(app);
      // Set persistence to LOCAL
      firebaseAuth.setPersistence(auth, firebaseAuth.browserLocalPersistence);
    }
    
    return auth;
  } catch (error) {
    // If Firebase fails to load (e.g., during SSR), return null
    if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
      console.warn('Firebase auth initialization failed:', error);
    }
    return null;
  }
};

// AuthProvider component - only initializes Firebase in browser
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mounted, setMounted] = useState(false);

  // Only run after component mounts (client-side only)
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    // Only run in browser after mount
    if (!mounted || typeof window === 'undefined') {
      setLoading(false);
      return;
    }

    let unsubscribe = null;

    // Initialize auth after mount
    const initAuth = () => {
      try {
        // Get auth instance (lazy initialization)
        const authInstance = getAuthInstance();
        if (!authInstance) {
          setLoading(false);
          return;
        }

        // Import onAuthStateChanged dynamically to avoid SSR issues
        const { onAuthStateChanged } = require('firebase/auth');

        // Listen for auth state changes
        unsubscribe = onAuthStateChanged(authInstance, (firebaseUser) => {
          setUser(firebaseUser);
          setLoading(false);
        });
      } catch (error) {
        if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
          console.warn('Auth initialization error:', error);
        }
        setLoading(false);
      }
    };

    // Use setTimeout to ensure we're fully in browser context
    const timeoutId = setTimeout(initAuth, 0);

    // Cleanup function
    return () => {
      clearTimeout(timeoutId);
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [mounted]);

  const value = {
    user,
    loading: !mounted || loading,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Export function to get auth instance for use in auth service
export const getAuth = () => getAuthInstance();
